import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Banknote,
  Building2,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Layers,
  ShieldCheck,
  TrendingUp,
  Wallet,
} from "lucide-react";
import {
  ELIGIBILITY,
  FAQ,
  FOOTER,
  HERO,
  NAV,
  PRODUCTS,
  STATS,
  STICKY,
  WHY,
} from "@/constants/testIds";

const AFN_PARTNER_ID = "SBLulC5NRbeYTe1BREqZlmYaJED2";
const AFN_IFRAME_URL = `https://app.advancefundsnetwork.com/partner-embed/${AFN_PARTNER_ID}?background=%23ffffff00&padding=20&color=`;
const AFN_APPLICATION_URL = `https://app.advancefundsnetwork.com/application/rhrzmK74jhhXZPzcEQ6QoWAbVrC3?reference=I2W1M4NrII1tWUck0cXf&partner=${AFN_PARTNER_ID}`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3 backdrop-blur-xl bg-[#04060a]/70 border-b border-white/5" : "py-5"
      }`}
    >
      <div className="container-x flex items-center justify-between">
        <a href="#top" data-testid={NAV.logo} className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md flex items-center justify-center border border-white/15 bg-gradient-to-br from-[#11162a] to-[#04060a]">
            <span className="font-serif text-[20px] text-[#C5A059] leading-none">Z</span>
          </div>
          <div className="leading-tight">
            <div className="font-serif text-[20px] tracking-tight text-white">Zentrix</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A1A6B4]">
              Funding
            </div>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          <a href="#products" data-testid={NAV.linkProducts} className="nav-link">
            Products
          </a>
          <a href="#eligibility" data-testid={NAV.linkEligibility} className="nav-link">
            Eligibility
          </a>
          <a href="#why" data-testid={NAV.linkWhy} className="nav-link">
            Why Zentrix
          </a>
          <a href="#faq" data-testid={NAV.linkFaq} className="nav-link">
            FAQ
          </a>
        </nav>
        <a
          href={AFN_APPLICATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={NAV.ctaApply}
          className="btn-gold rounded-full px-5 py-2.5 text-[13px] inline-flex items-center gap-2"
        >
          Apply Now <ArrowUpRight size={14} strokeWidth={2.5} />
        </a>
      </div>
    </header>
  );
};

const Hero = () => {
  const iframeRef = useRef(null);

  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== "https://app.advancefundsnetwork.com") return;
      if (event.data && event.data.iframeHeight && iframeRef.current) {
        iframeRef.current.style.height = event.data.iframeHeight + "px";
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="hero-bg relative pt-36 pb-24 md:pt-44 md:pb-32"
    >
      <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-7">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            className="label-overline mb-6 inline-flex items-center gap-2"
          >
            <span className="w-8 h-px bg-[#C5A059]" />
            Trusted Capital Partner · Since 2008
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            className="font-serif text-[44px] leading-[1.05] sm:text-[58px] md:text-[72px] tracking-tight text-white"
          >
            Stop waiting for capital.
            <br />
            <span className="shine-text italic">Accelerate</span> your business.
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            className="mt-7 max-w-xl text-[17px] md:text-[19px] text-[#A1A6B4] leading-relaxed font-light"
          >
            Secure <span className="text-white font-normal">$10,000 – $5,000,000</span> in
            same-day funding through Zentrix's institutional partner network. No upfront
            fees. Pre-qualify in under 60 seconds.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={3}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={AFN_APPLICATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={HERO.primaryCta}
              className="btn-gold rounded-full px-7 py-3.5 text-[14px] inline-flex items-center gap-2"
            >
              Start Full Application <ArrowUpRight size={16} strokeWidth={2.5} />
            </a>
            <a
              href="#lead-form"
              data-testid={HERO.secondaryCta}
              className="btn-ghost rounded-full px-7 py-3.5 text-[14px] inline-flex items-center gap-2"
            >
              Pre-Qualify Below
            </a>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={4}
            className="mt-12 grid grid-cols-3 gap-6 max-w-lg"
          >
            {[
              { k: "$2B+", v: "Funded" },
              { k: "4 hrs", v: "Avg. Funding" },
              { k: "30K+", v: "Businesses" },
            ].map((it) => (
              <div key={it.v}>
                <div className="font-serif text-[28px] md:text-[34px] text-white leading-none">
                  {it.k}
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[#A1A6B4]">
                  {it.v}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 w-full" id="lead-form">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="iframe-wrap"
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-[#C5A059]" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#A1A6B4]">
                  Secure Pre-Qualification
                </span>
              </div>
              <span className="text-[10px] text-[#6b7280]">60 sec · No credit pull</span>
            </div>
            <iframe
              ref={iframeRef}
              id="afn-iframe"
              data-testid={HERO.iframe}
              src={AFN_IFRAME_URL}
              title="Zentrix · Pre-Qualify for Funding"
              scrolling="no"
            />
          </motion.div>
          <p className="mt-4 text-[11px] text-[#6b7280] text-center leading-relaxed">
            Application processing technology by Advance Funds Network LLC. Your information
            is encrypted and routed to Zentrix's underwriting partners only.
          </p>
        </div>
      </div>
    </section>
  );
};

const TrustBar = () => (
  <section className="border-y border-white/5 bg-[#0b0f19]/40">
    <div className="container-x py-10 grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
      {[
        { Icon: ShieldCheck, t: "Bank-Grade Encryption" },
        { Icon: TrendingUp, t: "Funding in 4 Hours" },
        { Icon: Wallet, t: "No Upfront Fees" },
        { Icon: Building2, t: "USA & Canada Network" },
      ].map(({ Icon, t }) => (
        <div key={t} className="flex items-center gap-3">
          <Icon size={18} className="text-[#C5A059]" />
          <span className="text-[13px] text-[#A1A6B4] tracking-wide">{t}</span>
        </div>
      ))}
    </div>
  </section>
);

const products = [
  {
    key: "term",
    title: "Term Business Loans",
    desc: "Fixed-rate capital for expansion, equipment, and long-term moves. We look beyond credit scores.",
    Icon: Banknote,
    span: "b1",
    note: "From $25K · Up to 5 years",
  },
  {
    key: "wc",
    title: "Working Capital",
    desc: "Bridge cash flow gaps with speed.",
    Icon: CircleDollarSign,
    span: "b2",
    note: "4-hour funding",
  },
  {
    key: "loc",
    title: "Business Line of Credit",
    desc: "Draw what you need, when you need it. Flexible access for unexpected costs.",
    Icon: Layers,
    span: "b3",
    note: "Revolving · Pay-as-you-use",
  },
  {
    key: "mca",
    title: "Merchant Cash Advance",
    desc: "Future receivables, today. Simple, fast, revenue-based.",
    Icon: TrendingUp,
    span: "b4",
    note: "Same-day decisions",
  },
  {
    key: "debt",
    title: "Debt Consolidation",
    desc: "Combine multiple obligations into one streamlined payment.",
    Icon: Wallet,
    span: "b5",
    note: "Lower total payments",
  },
];

const Products = () => (
  <section id="products" data-testid={PRODUCTS.section} className="section">
    <div className="container-x">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div className="max-w-2xl">
          <div className="label-overline mb-4">Funding Products</div>
          <h2 className="font-serif text-[40px] md:text-[56px] tracking-tight leading-[1.05] text-white">
            A capital stack <span className="italic text-[#C5A059]">built for</span> your stage.
          </h2>
        </div>
        <p className="text-[15px] text-[#A1A6B4] max-w-md leading-relaxed">
          Five financing instruments, one underwriting team. Zentrix matches you with the
          structure that fits — not the one a bank wants to sell.
        </p>
      </div>

      <div className="bento">
        {products.map((p, i) => (
          <motion.div
            key={p.key}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            custom={i}
            data-testid={PRODUCTS.card(p.key)}
            className={`product-card ${p.span} flex flex-col justify-between`}
          >
            <div>
              <div className="w-11 h-11 rounded-md border border-white/10 bg-white/5 flex items-center justify-center mb-6">
                <p.Icon size={20} className="text-[#C5A059]" />
              </div>
              <h3 className="font-serif text-[26px] md:text-[30px] tracking-tight text-white leading-tight">
                {p.title}
              </h3>
              <p className="mt-3 text-[14px] text-[#A1A6B4] leading-relaxed max-w-md">
                {p.desc}
              </p>
            </div>
            <div className="mt-8 flex items-center justify-between border-t border-white/5 pt-5">
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#6b7280]">
                {p.note}
              </span>
              <a
                href={AFN_APPLICATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C5A059] hover:text-[#DCC287] transition-colors inline-flex items-center gap-1 text-[13px]"
              >
                Apply <ArrowUpRight size={14} />
              </a>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Eligibility = () => (
  <section
    id="eligibility"
    data-testid={ELIGIBILITY.section}
    className="section bg-[#0b0f19]/40 border-y border-white/5"
  >
    <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <div className="label-overline mb-4">Qualification</div>
        <h2 className="font-serif text-[40px] md:text-[52px] tracking-tight leading-[1.05] text-white">
          Simple requirements.
          <br />
          <span className="italic text-[#C5A059]">Faster</span> funding.
        </h2>
        <p className="mt-6 text-[15px] text-[#A1A6B4] max-w-md leading-relaxed">
          We believe more businesses deserve access to capital — so we keep qualification
          honest and accessible.
        </p>
        <a
          href={AFN_APPLICATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={ELIGIBILITY.cta}
          className="btn-gold rounded-full px-7 py-3.5 text-[14px] inline-flex items-center gap-2 mt-10"
        >
          Check My Eligibility <ArrowUpRight size={16} strokeWidth={2.5} />
        </a>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-3 gap-6">
        {[
          { n: "01", k: "1+ Year", v: "Operating history" },
          { n: "02", k: "$35K+", v: "Monthly revenue" },
          { n: "03", k: "500+", v: "FICO credit score" },
        ].map((it) => (
          <motion.div
            key={it.n}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="border border-white/10 rounded-lg p-7 bg-gradient-to-b from-white/[0.03] to-transparent"
          >
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#6b7280]">
              {it.n}
            </div>
            <div className="kbd-num mt-4">{it.k}</div>
            <div className="mt-3 text-[14px] text-[#A1A6B4]">{it.v}</div>
            <CheckCircle2 size={16} className="text-[#C5A059] mt-6" />
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const WhyZentrix = () => (
  <section id="why" data-testid={WHY.section} className="section">
    <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      <div className="lg:col-span-6 order-2 lg:order-1">
        <div className="relative">
          <div className="aspect-[4/5] rounded-lg overflow-hidden border border-white/10">
            <img
              src="https://images.pexels.com/photos/4428045/pexels-photo-4428045.jpeg"
              alt="Zentrix founder portrait"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 glass rounded-lg px-6 py-5 max-w-[260px] hidden md:block">
            <div className="label-overline mb-2">A Note From The Desk</div>
            <p className="font-serif italic text-[17px] text-white leading-snug">
              "Capital should arrive when opportunity does — not three weeks later."
            </p>
          </div>
        </div>
      </div>
      <div className="lg:col-span-6 order-1 lg:order-2">
        <div className="label-overline mb-4">Why Zentrix</div>
        <h2 className="font-serif text-[40px] md:text-[56px] tracking-tight leading-[1.05] text-white">
          We don't sell loans.
          <br />
          We <span className="italic text-[#C5A059]">protect momentum.</span>
        </h2>
        <p className="mt-7 text-[16px] text-[#A1A6B4] leading-relaxed">
          Zentrix Platform is an independent funding consultancy built for operators who
          refuse to let liquidity slow them down. We've assembled an institutional partner
          network — anchored by Advance Funds Network — to deliver the kind of speed,
          discretion, and structure that family offices and private credit desks reserve
          for their own.
        </p>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          {[
            "Concierge-grade application support",
            "Access to 75+ underwriting partners",
            "No upfront fees — ever",
            "Tailored capital, not templated quotes",
          ].map((t) => (
            <div key={t} className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-[#C5A059] mt-0.5 shrink-0" />
              <span className="text-[14px] text-[#D6DAE3]">{t}</span>
            </div>
          ))}
        </div>
        <a
          href={AFN_APPLICATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={WHY.cta}
          className="btn-ghost rounded-full px-7 py-3.5 text-[14px] inline-flex items-center gap-2 mt-12"
        >
          Begin a Conversation <ArrowUpRight size={16} />
        </a>
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section data-testid={STATS.section} className="section bg-[#0b0f19]/40 border-y border-white/5">
    <div className="container-x">
      <div className="grid md:grid-cols-4 gap-10 md:gap-6">
        {[
          { k: "2008", v: "Network founded" },
          { k: "$2B+", v: "Deployed to operators" },
          { k: "4 hrs", v: "Median funding time" },
          { k: "75+", v: "Capital partners" },
        ].map((s) => (
          <motion.div
            key={s.v}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="border-l border-white/10 pl-6"
          >
            <div className="kbd-num">{s.k}</div>
            <div className="mt-4 text-[12px] uppercase tracking-[0.22em] text-[#A1A6B4]">
              {s.v}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const faqs = [
  {
    q: "What types of business funding can I get through Zentrix?",
    a: "Through our partner network we structure Term Loans, Working Capital advances, Business Lines of Credit, Merchant Cash Advances, and Debt Consolidation facilities — ranging from $10K to $5M.",
  },
  {
    q: "How quickly can I receive funding?",
    a: "Most qualified applicants receive a decision the same day and funds in their account within 4 hours after final approval. Larger structured facilities can take 24–72 hours.",
  },
  {
    q: "What are the minimum requirements?",
    a: "We require a minimum operational history of 1 year, monthly revenue of at least $35,000, and a FICO score of 500 or higher. Startups under 12 months are not currently eligible.",
  },
  {
    q: "Will applying hurt my credit?",
    a: "Pre-qualification uses a soft pull only. A hard inquiry is only performed once you accept terms with a specific lending partner.",
  },
  {
    q: "Are there any upfront fees?",
    a: "No. Zentrix and Advance Funds Network never charge upfront fees. We are compensated by the lending partner upon successful funding.",
  },
  {
    q: "How is my information protected?",
    a: "All data is transmitted over encrypted channels and stored under bank-grade controls within the Advance Funds Network underwriting platform.",
  },
];

const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="faq-item" data-testid={FAQ.item(idx)}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-6 py-7 text-left group"
      >
        <span className="font-serif text-[22px] md:text-[26px] text-white tracking-tight leading-snug">
          {q}
        </span>
        <ChevronDown
          size={20}
          className={`text-[#C5A059] shrink-0 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? "max-h-60 pb-7" : "max-h-0"
        }`}
      >
        <p className="text-[15px] text-[#A1A6B4] leading-relaxed max-w-3xl">{a}</p>
      </div>
    </div>
  );
};

const FAQs = () => (
  <section id="faq" data-testid={FAQ.section} className="section">
    <div className="container-x grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <div className="label-overline mb-4">Frequently Asked</div>
        <h2 className="font-serif text-[40px] md:text-[52px] tracking-tight leading-[1.05] text-white">
          Clarity, <span className="italic text-[#C5A059]">first.</span>
        </h2>
        <p className="mt-6 text-[15px] text-[#A1A6B4] leading-relaxed max-w-sm">
          The answers most operators want before they trust a partner with their capital
          strategy.
        </p>
      </div>
      <div className="lg:col-span-8">
        {faqs.map((f, i) => (
          <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />
        ))}
      </div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="section pb-32">
    <div className="container-x">
      <div className="relative overflow-hidden rounded-2xl border border-white/10 p-10 md:p-20 bg-gradient-to-br from-[#0b0f19] via-[#11162a] to-[#04060a]">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            background:
              "radial-gradient(600px 300px at 80% 30%, rgba(197,160,89,0.25), transparent 60%), radial-gradient(500px 250px at 10% 80%, rgba(197,160,89,0.15), transparent 60%)",
          }}
        />
        <div className="relative max-w-3xl">
          <div className="label-overline mb-5">The Next Move</div>
          <h2 className="font-serif text-[40px] md:text-[64px] tracking-tight leading-[1.02] text-white">
            Your capital is <span className="italic text-[#C5A059]">waiting.</span> All
            that's left is the application.
          </h2>
          <p className="mt-7 text-[16px] text-[#A1A6B4] max-w-xl leading-relaxed">
            Pre-qualify in 60 seconds. No credit pull. No upfront fees. A real human from
            Zentrix follows up within the hour.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={AFN_APPLICATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={FOOTER.ctaApply}
              className="btn-gold rounded-full px-8 py-4 text-[14px] inline-flex items-center gap-2"
            >
              Start Application <ArrowUpRight size={16} strokeWidth={2.5} />
            </a>
            <a
              href="#lead-form"
              className="btn-ghost rounded-full px-8 py-4 text-[14px] inline-flex items-center gap-2"
            >
              Pre-Qualify Instead
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer data-testid={FOOTER.section} className="border-t border-white/5">
    <div className="container-x py-16 grid md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md flex items-center justify-center border border-white/15 bg-gradient-to-br from-[#11162a] to-[#04060a]">
            <span className="font-serif text-[20px] text-[#C5A059] leading-none">Z</span>
          </div>
          <div className="leading-tight">
            <div className="font-serif text-[20px] tracking-tight text-white">Zentrix</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A1A6B4]">
              Funding
            </div>
          </div>
        </div>
        <p className="mt-6 text-[13px] text-[#A1A6B4] max-w-sm leading-relaxed">
          Independent business funding consultancy serving operators across the United
          States and Canada.
        </p>
      </div>
      <div className="md:col-span-4">
        <div className="label-overline mb-4">Explore</div>
        <ul className="space-y-3 text-[13px] text-[#A1A6B4]">
          <li><a href="#products" className="nav-link">Funding Products</a></li>
          <li><a href="#eligibility" className="nav-link">Eligibility</a></li>
          <li><a href="#why" className="nav-link">Why Zentrix</a></li>
          <li><a href="#faq" className="nav-link">FAQ</a></li>
        </ul>
      </div>
      <div className="md:col-span-3">
        <div className="label-overline mb-4">Apply</div>
        <a
          href={AFN_APPLICATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold rounded-full px-5 py-2.5 text-[13px] inline-flex items-center gap-2"
        >
          Start Application <ArrowUpRight size={14} />
        </a>
      </div>
    </div>
    <div className="border-t border-white/5">
      <div className="container-x py-8 flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-[11px] text-[#6b7280] leading-relaxed">
        <p className="max-w-3xl">
          Application processing technology provided by Advance Funds Network LLC. All
          marketing and customer outreach is conducted solely by Zentrix Platform, an
          independent business. Not all applicants will qualify. Approval and funding terms
          subject to underwriting.
        </p>
        <p>© {new Date().getFullYear()} Zentrix Platform. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

const StickyMobileCTA = () => (
  <a
    href={AFN_APPLICATION_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={STICKY.apply}
    className="sticky-mobile-cta"
  >
    <span className="btn-gold rounded-full w-full py-3.5 text-[14px] inline-flex items-center justify-center gap-2">
      Start Funding Application <ArrowUpRight size={16} strokeWidth={2.5} />
    </span>
  </a>
);

const Landing = () => {
  useEffect(() => {
    document.title =
      "Zentrix Funding · Same-Day Business Capital from $10K to $5M";
  }, []);

  return (
    <>
      <Nav />
      <Hero />
      <TrustBar />
      <Products />
      <Eligibility />
      <WhyZentrix />
      <Stats />
      <FAQs />
      <FinalCTA />
      <Footer />
      <StickyMobileCTA />
    </>
  );
};

export default Landing;
